/**
 * Lista de entrevistas del Hiring Manager
 * Muestra todas las entrevistas programadas con filtros y acciones
 */

import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState } from "react";
import {
  Calendar,
  Search,
  CheckCircle,
  CheckCheck,
  CircleX,
} from "lucide-react";
import type { HiringManagerInterview } from '@/types/hiringManagerInterview';
import { useNavigate } from "react-router-dom";
import { hiringManagerInterviewService } from '@/features/rh/api/hiringManagerInterviewService';
import toast from "react-hot-toast";
import { nombreDeUsuario } from "@/shared/lib/nombreDeUsuario";
import { Badge } from '@/shared/components/Badge';
import FilterSelect from '@/shared/components/FilterSelect';
import {
  dentroDelRango,
  esperaDecisionDelSolicitante,
} from '@/features/rh/lib/rangoDeFechas';
import {
  decisionDeLaEntrevista,
  etiquetaDeEstatus,
} from '@/features/rh/lib/estatusDelCandidato';
import { formatDate } from '@/shared/lib/dateUtils';
import { EsqueletoTabla } from '@/shared/components/Esqueleto';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

interface HiringManagerInterviewsListProps {
  interviews: HiringManagerInterview[];
  isLoading?: boolean;
  onViewDetail: (interview: HiringManagerInterview) => void;
  onRecordDecision: (interview: HiringManagerInterview) => void;
  onDelete: (id: number) => void;
  onRefresh?: () => void;
}

export const HiringManagerInterviewsList: React.FC<
  HiringManagerInterviewsListProps
> = ({
  interviews,
  isLoading = false,
  onRefresh,
  // onViewDetail,
  // onRecordDecision,
  // onDelete,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedModalidad, setSelectedModalidad] = useState<string>("");
  const [selectedEtapa, setSelectedEtapa] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleViewCandidateProfile = (candidateId: string) => {
    // Navegar al perfil del candidato
    navigate(`/candidate/${candidateId}`);
  };

  const handleApprove = async (
    e: React.MouseEvent,
    interview: HiringManagerInterview,
  ) => {
    e.stopPropagation();

    try {
      // Registrar la decisión como "aprobado" usando el endpoint correcto
      await hiringManagerInterviewService.recordDecision(interview.id, {
        decision: "aprobado",
        comentarios: "Aprobado desde la lista de entrevistas",
      });

      // Refrescar la lista de entrevistas
      if (onRefresh) {
        onRefresh();
      }

      // Mostrar modal de éxito
      setSuccessMessage(
        `El candidato ${interview.candidate.fullName} ha sido aprobado exitosamente`,
      );
      setShowSuccessModal(true);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al aprobar candidato:", err);
      toast.error(
        "Error al aprobar el candidato: " +
          (err.message || "Error desconocido"),
      );
    }
  };

  const handleReject = async (
    e: React.MouseEvent,
    interview: HiringManagerInterview,
  ) => {
    e.stopPropagation();

    // Para rechazar, pedimos comentarios
    const comentarios = prompt(
      "Por favor, ingresa los comentarios del rechazo (obligatorio):",
    );

    if (!comentarios || comentarios.trim() === "") {
      toast.error(
        "Los comentarios son obligatorios para rechazar un candidato",
      );
      return;
    }

    try {
      // Registrar la decisión como "rechazado"
      await hiringManagerInterviewService.recordDecision(interview.id, {
        decision: "rechazado",
        comentarios: comentarios.trim(),
      });

      // Refrescar la lista de entrevistas
      if (onRefresh) {
        onRefresh();
      }

      // Mostrar modal de éxito
      setSuccessMessage(
        `El candidato ${interview.candidate.fullName} ha sido rechazado`,
      );
      setShowSuccessModal(true);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al rechazar candidato:", err);
      toast.error(
        "Error al rechazar el candidato: " +
          (err.message || "Error desconocido"),
      );
    }
  };

  // Filtrar entrevistas
  const filteredInterviews = interviews.filter((interview) => {
    const matchesSearch =
      searchTerm === "" ||
      interview.candidate.fullName
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      interview.area.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nombreDeUsuario(
        interview.entrevistador ?? interview.entrevistadorHistorico,
      )
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      selectedStatus === "" || interview.decision === selectedStatus;
    const matchesModalidad =
      selectedModalidad === "" || interview.modalidad === selectedModalidad;
    const matchesEtapa =
      selectedEtapa === "" || interview.candidate?.status === selectedEtapa;
    const matchesFecha = dentroDelRango(
      interview.fechaEntrevista,
      fechaDesde,
      fechaHasta,
    );

    return (
      matchesSearch &&
      matchesStatus &&
      matchesModalidad &&
      matchesEtapa &&
      matchesFecha
    );
  });

  // Solo las etapas que de verdad aparecen en la tabla: ofrecer una que ninguna
  // fila tiene devolvería una lista vacía.
  const opcionesDeEtapa = [
    ...new Set(
      interviews
        .map((interview) => String(interview.candidate?.status ?? ""))
        .filter(Boolean),
    ),
  ].map((status) => ({ value: status, label: etiquetaDeEstatus(status).label }));

  const hayFiltros = Boolean(
    searchTerm ||
      selectedStatus ||
      selectedModalidad ||
      selectedEtapa ||
      fechaDesde ||
      fechaHasta,
  );

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedEtapa("");
    setSelectedStatus("");
    setSelectedModalidad("");
    setFechaDesde("");
    setFechaHasta("");
  };

  /**
   * La pastilla de la columna Estatus: lo que resolvió ESTA entrevista, con los
   * mismos tres valores que el filtro de arriba. Sin icono: en una tabla
   * repetiria lo que ya dice la etiqueta.
   */
  const getStatusBadge = (interview: HiringManagerInterview) => {
    const config = decisionDeLaEntrevista(interview.decision);

    return <Badge color={config.color}>{config.label}</Badge>;
  };

  /**
   * Dónde va el candidato hoy. Aparte de la decisión porque son dos preguntas:
   * la entrevista se queda en «Aprobado» aunque el candidato siga avanzando.
   */
  const getEtapaBadge = (interview: HiringManagerInterview) => {
    const config = etiquetaDeEstatus(interview.candidate?.status);

    return <Badge color={config.color}>{config.label}</Badge>;
  };

  // A-01: silueta de la tabla; el spinner dejaba la pantalla en blanco.
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        <EsqueletoTabla filas={6} columnas={8} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros con el patrón de almacén: una fila blanca con borde, el
          buscador con lupa y los desplegables del componente compartido. */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en entrevistas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:border-[#315FB9]"
          />
        </div>

        <FilterSelect
          label="Estatus"
          ancho="contenido"
          options={[
            { value: 'pendiente', label: 'Pendiente' },
            { value: 'aprobado', label: 'Aprobado' },
            { value: 'rechazado', label: 'Rechazado' },
          ]}
          value={selectedStatus}
          onChange={setSelectedStatus}
        />

        <FilterSelect
          label="Modalidad"
          ancho="contenido"
          options={[
            { value: 'presencial', label: 'Presencial' },
            { value: 'virtual', label: 'Virtual' },
          ]}
          value={selectedModalidad}
          onChange={setSelectedModalidad}
        />

        <FilterSelect
          label="Etapa del candidato"
          ancho="contenido"
          options={opcionesDeEtapa}
          value={selectedEtapa}
          onChange={setSelectedEtapa}
        />

        {/* Rango de fechas de la entrevista, con el input de almacén: calendario
            azul por date-brand y colorScheme fijo en claro para que el control
            nativo no se invierta con el tema del sistema. Cada extremo acota al
            otro, para no poder capturar un rango al revés que no da resultados. */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            aria-label="Entrevistas desde"
            value={fechaDesde}
            max={fechaHasta || undefined}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B5998] w-[160px] date-brand"
            style={{ colorScheme: "light" }}
          />
          <span className="text-sm text-gray-400">–</span>
          <input
            type="date"
            aria-label="Entrevistas hasta"
            value={fechaHasta}
            min={fechaDesde || undefined}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B5998] w-[160px] date-brand"
            style={{ colorScheme: "light" }}
          />
        </div>

        {hayFiltros && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="text-sm font-semibold text-[#315FB9] hover:underline"
          >
            Limpiar
          </button>
        )}

        <span className="ml-auto text-sm text-gray-500">
          {filteredInterviews.length} entrevistas
        </span>
      </div>

      {/* Tabla de entrevistas */}
      {/* Sin alto minimo: con dos registros la tarjeta ocupaba 700px de
          alto vacio. El menu de acciones ya no lo necesita porque se pinta
          por encima con posicion absoluta. */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB] relative">
        {filteredInterviews.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay entrevistas programadas
            </h3>
            <p className="text-gray-600">
              {hayFiltros
                ? "No se encontraron entrevistas con los filtros aplicados."
                : "Aún no hay entrevistas programadas."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E5E7EB] relative z-10">
              <thead className="bg-[#FAF9FF]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Acciones
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Candidato
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Vacante
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Hora
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Modalidad
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Entrevistador
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Estatus
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Etapa del candidato
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E1E1E1]">
                {filteredInterviews.map((interview) => (
                  <tr key={interview.id} className="hover:bg-[#F4F7FB] transition-colors">
                    {/* Aprobar y rechazar directo, sin menú. Solo mientras la
                        entrevista sigue pendiente: ya decidida no hay nada que
                        resolver. El resto de la fila lleva al perfil del
                        candidato, que era la otra opción del menú. */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {esperaDecisionDelSolicitante(interview) && (
                        <div className="inline-flex items-center gap-2">
                          {/* El globo sale a la derecha y no arriba: la tabla va
                              dentro de un contenedor con overflow oculto, y
                              hacia arriba quedaria recortado. Como Acciones es
                              la primera columna, a la derecha siempre hay tabla
                              de sobra. */}
                          <span className="group relative inline-flex">
                            <button
                              type="button"
                              onClick={(e) => handleApprove(e, interview)}
                              aria-label="Aprobar candidato"
                              className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#1CB271] text-white hover:opacity-90 transition-opacity"
                            >
                              <CheckCheck className="w-4 h-4" />
                            </button>
                            <span className="pointer-events-none absolute left-full top-1/2 z-20 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white opacity-0 invisible transition-all duration-200 group-hover:visible group-hover:opacity-100">
                              Aprobar candidato
                              <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                            </span>
                          </span>

                          <span className="group relative inline-flex">
                            <button
                              type="button"
                              onClick={(e) => handleReject(e, interview)}
                              aria-label="Rechazar candidato"
                              className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#F8E3E3] text-[#EF4444] hover:opacity-90 transition-opacity"
                            >
                              <CircleX className="w-4 h-4" />
                            </button>
                            <span className="pointer-events-none absolute left-full top-1/2 z-20 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white opacity-0 invisible transition-all duration-200 group-hover:visible group-hover:opacity-100">
                              Rechazar candidato
                              <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                            </span>
                          </span>
                        </div>
                      )}
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        handleViewCandidateProfile(interview.candidate.id)
                      }
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {interview.candidate.fullName}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        handleViewCandidateProfile(interview.candidate.id)
                      }
                    >
                      <div className="text-sm text-gray-900">
                        {interview.candidate.email}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        handleViewCandidateProfile(interview.candidate.id)
                      }
                    >
                      <div className="text-sm text-gray-900">
                        {interview.candidate.jobRequest?.puestoSolicitar || "Sin vacante"}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        handleViewCandidateProfile(interview.candidate.id)
                      }
                    >
                      <div className="text-sm text-gray-900">
                        {formatDate(interview.fechaEntrevista)}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        handleViewCandidateProfile(interview.candidate.id)
                      }
                    >
                      <div className="text-sm text-gray-900">
                        {interview.horaEntrevista}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        handleViewCandidateProfile(interview.candidate.id)
                      }
                    >
                      <div className="text-sm text-gray-900 capitalize">
                        {interview.modalidad}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        handleViewCandidateProfile(interview.candidate.id)
                      }
                    >
                      <div className="text-sm text-gray-900">
                        {nombreDeUsuario(
                          interview.entrevistador ?? interview.entrevistadorHistorico,
                        )}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        handleViewCandidateProfile(interview.candidate.id)
                      }
                    >
                      {getStatusBadge(interview)}
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap cursor-pointer"
                      onClick={() =>
                        handleViewCandidateProfile(interview.candidate.id)
                      }
                    >
                      {getEtapaBadge(interview)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de éxito */}
      {showSuccessModal && (
        <CapaDeModal zIndex={100000}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>

            <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
              ¡Operación exitosa!
            </h3>

            <p className="text-gray-600 text-center mb-6">{successMessage}</p>

            <button
              onClick={() => {
                setShowSuccessModal(false);
                setSuccessMessage("");
              }}
              className={estiloDeBoton('exito', 'w-full')}
            >
              Aceptar
            </button>
          </div>
        </CapaDeModal>
      )}
    </div>
  );
};
